import Helmet from 'react-helmet';
import mapbox from 'mapbox-gl';
import MapboxLanguage from '@mapbox/mapbox-gl-language';
import PropTypes from 'prop-types';
import React from 'react';
import styled from 'styled-components';
import { connect } from 'react-redux';
import './salon-map.css';

mapbox.accessToken = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN;

const StyledMap = styled.div`
  height: 100%;
  width: 100%;

  @media (min-width: 900px) {
    width: calc(100% - 300px);
  }
`;

class Map extends React.Component {
  static defaultProps = {
    focus: -1,
  };

  static propTypes = {
    coords: PropTypes.shape().isRequired,
    copy: PropTypes.shape().isRequired,
    focus: PropTypes.number,
    locale: PropTypes.string.isRequired,
    salons: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  };

  componentWillMount() {
    this.createMarkers();
  }

  componentDidMount() {
    const { locale } = this.props;
    this.map = new mapbox.Map({
      container: 'salon-map',
      logoPosition: 'top-left',
      scrollZoom: false,
      style: 'mapbox://styles/mapbox/light-v9',
    });
    this.map.addControl(new mapbox.NavigationControl(), 'top-right');
    this.map.addControl(new mapbox.ScaleControl());
    this.map.addControl(new MapboxLanguage({ defaultLanguage: locale.slice(0, 2) }));
    this.markers.forEach(marker => marker.addTo(this.map));

    this.map.on('load', () => {
      this.boundMap();
    });
  }

  componentDidUpdate() {
    const { focus, salons } = this.props;

    if (focus >= 0) {
      const salon = salons[focus];
      this.map.flyTo({
        center: [salon.location.lon, salon.location.lat + 0.001],
        zoom: 15,
        curve: 1,
      });
      this.markers.forEach((marker, i) => {
        const popup = marker.getPopup();
        if (popup && (i === focus) !== popup.isOpen()) marker.togglePopup();
      });
    } else {
      this.markers.forEach(marker => marker.remove());
      this.createMarkers();
      this.markers.forEach(marker => marker.addTo(this.map));
      this.boundMap();
    }
  }

  boundMap = () => {
    const { coords, salons } = this.props;

    if (salons.length === 0) {
      this.map.flyTo({
        center: [coords.lon, coords.lat],
        zoom: 10,
      });
    } else {
      const locations = salons.map(salon => salon.location);
      locations.push(coords);

      const bounds = [
        [Math.min(...locations.map(l => l.lon)), Math.min(...locations.map(l => l.lat))],
        [Math.max(...locations.map(l => l.lon)), Math.max(...locations.map(l => l.lat))],
      ];
      this.map.fitBounds(bounds, {
        padding: 20 + Math.min(100, Math.round(window.innerWidth / 20)),
      });
    }
  };

  createMarkers = () => {
    const { coords, salons } = this.props;
    this.markers = salons.map(salon => {
      const marker = new mapbox.Marker().setLngLat([salon.location.lon, salon.location.lat]);
      const div = this.createPopup(salon);
      const popup = new mapbox.Popup({ anchor: 'bottom' }).setDOMContent(div);
      marker.setPopup(popup);
      return marker;
    });
    const userMarker = new mapbox.Marker({ color: 'orange' }).setLngLat([coords.lon, coords.lat]);
    this.markers.push(userMarker);
  };

  createPopup = salon => {
    const { copy } = this.props;

    const popup = document.createElement('div');
    popup.className = 'salon-map-popup';

    const name = document.createElement('div');
    name.className = 'salon-map-popup__name';
    name.innerText = salon.name;
    popup.appendChild(name);
    if (salon.address1 || salon.address2) {
      const address = document.createElement('div');
      address.className = 'salon-map-popup__detail';
      // address.innerText = [salon.address1, salon.address2, salon.zipCode].filter(field => !!field).join(' ');
      const addrQueryText =
        salon.country === '日本'
          ? [salon.zipCode, salon.address2, salon.address1].filter(field => !!field).join(' ')
          : [salon.address1, salon.address2, salon.zipCode].filter(field => !!field).join(' ');
      const addrText =
        salon.country === '日本'
          ? [`〒${salon.zipCode}`, salon.address2, salon.address1].filter(field => !!field).join(' ')
          : [salon.address1, salon.address2, salon.zipCode].filter(field => !!field).join(' ');
      address.innerHTML = `<a href='https://maps.google.co.jp/maps?q=${addrQueryText} ${
        salon.name
      }' target='_blank'>${addrText}</a>`;
      popup.appendChild(address);
    }
    if (salon.nearbyStations) {
      const stations = document.createElement('div');
      stations.className = 'salon-map-popup__detail';
      stations.innerText = `${copy.nearby_stations}: ${salon.nearbyStations.join(', ')}`;
      popup.appendChild(stations);
    }
    if (salon.phoneNumber) {
      const phone = document.createElement('div');
      phone.className = 'salon-map-popup__detail';
      const phoneLabel = document.createElement('span');
      phoneLabel.className = 'phoneLabel';
      phoneLabel.innerHTML = `<span>${copy.phone_number}:<span>`;
      const phoneValue = document.createElement('a');
      phoneValue.innerText = salon.phoneNumber;
      phoneValue.href = `tel:${salon.phoneNumber}`;
      phone.appendChild(phoneLabel);
      phone.appendChild(phoneValue);
      popup.appendChild(phone);
    }
    if (salon.website) {
      const website = document.createElement('div');
      website.className = 'salon-map-popup__detail';
      website.innerText = `${copy.website}: `;
      const link = document.createElement('a');
      link.className = 'salon-map-popup__link';
      link.href = salon.website;
      link.innerText = salon.website;
      link.target = '_blank';
      website.appendChild(link);
      popup.appendChild(website);
    }
    if ('parking' in salon) {
      const parking = document.createElement('div');
      parking.className = 'salon-map-popup__detail';
      parking.innerText = `${copy.has_parking}: ${salon.parking ? copy.yes : copy.no}`;
      popup.appendChild(parking);
    }

    return popup;
  };

  render() {
    return (
      <StyledMap id="salon-map">
        <Helmet>
          <link href="https://api.tiles.mapbox.com/mapbox-gl-js/v0.45.0/mapbox-gl.css" rel="stylesheet" />
        </Helmet>
      </StyledMap>
    );
  }
}

const mapState = state => ({
  locale: state.locale,
});

export default connect(mapState)(Map);



// WEBPACK FOOTER //
// ./src/pages/SalonLocator/ResultSection/Map.js